// Turns the photos the user picked (one per page) into what the scanner
// needs: page images small enough to send (JPEG, ≤ 1600 px) and the original
// to keep. Photos only — PDFs aren't read.
// See docs/plans/inventory_and_costing/06_capture.md.

export const MAX_PAGES = 4;
export const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.82;

export type PreparedDocument = {
  /** data:image/jpeg;base64,… per page, for the AI and for previews. */
  pages: string[];
  /** What to upload as the original. */
  original: File;
};

export class PrepareError extends Error {}

export const isAcceptedImage = (f: File) => ACCEPTED_TYPES.includes(f.type);

export async function prepareDocument(files: File[]): Promise<PreparedDocument> {
  const photos = files.filter(isAcceptedImage).slice(0, MAX_PAGES);
  if (photos.length === 0) throw new PrepareError("no_files");
  const images = await Promise.all(photos.map(loadImage));
  const pages = images.map((img) => toJpeg(img));
  // Several photos are kept as one tall image, so the record has every page.
  const original = photos.length === 1 ? photos[0] : await canvasToFile(stack(images), "pages.jpg");
  return { pages, original };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    // HEIC opens only in Safari; elsewhere ask for a JPEG.
    img.onerror = () => reject(new PrepareError("unreadable_image"));
    img.src = url;
  });
}

function fit(w: number, h: number) {
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function toJpeg(img: HTMLImageElement) {
  const { width, height } = fit(img.naturalWidth, img.naturalHeight);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
}

function stack(images: HTMLImageElement[]) {
  const sizes = images.map((img) => fit(img.naturalWidth, img.naturalHeight));
  const width = Math.max(...sizes.map((s) => s.width));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = sizes.reduce((sum, s) => sum + s.height, 0);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  let y = 0;
  images.forEach((img, i) => {
    ctx.drawImage(img, 0, y, sizes[i].width, sizes[i].height);
    y += sizes[i].height;
  });
  return canvas;
}

function canvasToFile(canvas: HTMLCanvasElement, name: string): Promise<File> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(new File([blob], name, { type: "image/jpeg" })) : reject(new PrepareError("unreadable_image"))),
      "image/jpeg",
      JPEG_QUALITY,
    ),
  );
}
